// src/components/analytics/HeatMap.tsx
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from 'lucide-react';
import { Tooltip } from '@/components/ui/tooltip';

interface HeatMapProps {
  data: Array<{ date: string; count: number }>;
}

export const HeatMap: React.FC<HeatMapProps> = ({ data }) => {
  const getIntensityColor = (count: number): string => {
    if (count === 0) return 'bg-muted';
    if (count <= 2) return 'bg-green-500/30';
    if (count <= 5) return 'bg-green-500/50';
    if (count <= 10) return 'bg-green-500/70';
    return 'bg-green-500';
  };

  // Generate last 12 weeks of data
  const weeks: Array<Array<{ date: string; count: number }>> = [];
  const today = new Date();
  
  for (let week = 11; week >= 0; week--) {
    const weekData = [];
    for (let day = 0; day < 7; day++) {
      const date = new Date(today);
      date.setDate(date.getDate() - (week * 7 + day));
      const dateStr = date.toISOString().split('T')[0];
      const entry = data.find(d => d.date === dateStr) || { date: dateStr, count: 0 };
      weekData.push(entry);
    }
    weeks.push(weekData);
  }

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Activity Heatmap (Last 12 Weeks)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            <div className="flex gap-1">
              <div className="flex flex-col justify-around text-xs text-muted-foreground pr-2">
                {dayLabels.map((day, i) => (
                  <div key={day} className="h-3" style={{ lineHeight: '12px' }}>
                    {i % 2 === 0 ? day : ''}
                  </div>
                ))}
              </div>
              <div className="flex-1">
                <div className="flex gap-1">
                  {weeks.map((week, weekIndex) => (
                    <div key={weekIndex} className="flex flex-col gap-1">
                      {week.map((day) => (
                        <Tooltip key={day.date} content={`${day.date}: ${day.count} tests`}>
                          <div
                            className={`w-3 h-3 rounded-sm ${getIntensityColor(day.count)} hover:ring-2 hover:ring-primary transition-all cursor-pointer`}
                          />
                        </Tooltip>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
              <span>Less</span>
              {[0, 1, 2, 3, 4].map((level) => (
                <div
                  key={level}
                  className={`w-3 h-3 rounded-sm ${getIntensityColor(level * 3)}`}
                />
              ))}
              <span>More</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

